// ─────────────────────────────────────────────────────────────────────
// Offline puzzle cache (pinned tier)
// ─────────────────────────────────────────────────────────────────────
//
// IndexedDB-backed mirror of iOS's PuzzleCache pinned tier
// (App/Hexponents/Hexponents/PuzzleCache.swift). One object store
// holding raw JSON bytes (Blob) for `(date, difficulty)` puzzles
// downloaded by premium users for offline play.
//
// • Database:    'hexponents'
// • Object store: 'puzzles_pinned'
// • Key:          `${date}|${difficulty}` (e.g. "2026-03-04|Hard")
// • Value:        { date, difficulty, blob: Blob, size: number }
//
// Storing raw bytes (rather than a re-encoded Puzzle) matches iOS —
// a future schema change can fail-and-refetch at the decode site
// without ever crashing on a stale on-disk row.
//
// No external deps (no `idb`); the surface here is small enough that
// the native IDB API is fine and saves ~3KB gzip.

import type { Difficulty, Puzzle } from '../types';

const DB_NAME = 'hexponents';
const DB_VERSION = 1;
const STORE = 'puzzles_pinned';

interface PinnedRow {
  key: string;       // `${date}|${difficulty}` — keyPath
  date: string;
  difficulty: Difficulty;
  blob: Blob;        // raw JSON bytes
  size: number;      // duplicated for fast byte-sum without reading every blob
}

function compositeKey(date: string, difficulty: Difficulty): string {
  return `${date}|${difficulty}`;
}

// ─── DB open (memoised) ───────────────────────────────────────────────
// Each call returns the same promise so we don't spin up parallel
// `indexedDB.open` requests under bursty downloader load.
let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    req.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });
  // If the open fails, allow a retry on the next call rather than
  // permanently latching a rejected promise.
  dbPromise.catch(() => { dbPromise = null; });
  return dbPromise;
}

// Thin Promise wrapper around an IDBRequest.
function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error('IndexedDB request failed'));
  });
}

// ─── Public API ───────────────────────────────────────────────────────

export async function readPinned(date: string, difficulty: Difficulty): Promise<Puzzle | null> {
  let db: IDBDatabase;
  try { db = await openDB(); } catch { return null; }
  try {
    const tx = db.transaction(STORE, 'readonly');
    const row = await req<PinnedRow | undefined>(tx.objectStore(STORE).get(compositeKey(date, difficulty)));
    if (!row) return null;
    const text = await row.blob.text();
    return JSON.parse(text) as Puzzle;
  } catch {
    // Corrupt row / bad JSON → treat as miss. Caller falls through to
    // network; a successful refetch will overwrite the bad row.
    return null;
  }
}

export async function writePinned(date: string, difficulty: Difficulty, puzzle: Puzzle): Promise<void> {
  const db = await openDB();
  const json = JSON.stringify(puzzle);
  const blob = new Blob([json], { type: 'application/json' });
  const row: PinnedRow = {
    key: compositeKey(date, difficulty),
    date,
    difficulty,
    blob,
    size: blob.size,
  };
  const tx = db.transaction(STORE, 'readwrite');
  await req(tx.objectStore(STORE).put(row));
  await txDone(tx);
}

export async function isPinned(date: string, difficulty: Difficulty): Promise<boolean> {
  let db: IDBDatabase;
  try { db = await openDB(); } catch { return false; }
  try {
    const tx = db.transaction(STORE, 'readonly');
    // `getKey` avoids materialising the blob just to check existence.
    const k = await req<IDBValidKey | undefined>(tx.objectStore(STORE).getKey(compositeKey(date, difficulty)));
    return k != null;
  } catch { return false; }
}

/// Distinct set of dates that have at least one pinned difficulty.
/// Mirrors iOS pinnedDateSet — useful for cheap per-row indicators in
/// the archive list.
export async function pinnedDateSet(): Promise<Set<string>> {
  const out = new Set<string>();
  let db: IDBDatabase;
  try { db = await openDB(); } catch { return out; }
  try {
    const tx = db.transaction(STORE, 'readonly');
    const keys = await req<IDBValidKey[]>(tx.objectStore(STORE).getAllKeys());
    for (const k of keys) {
      const s = String(k);
      const sep = s.indexOf('|');
      if (sep > 0) out.add(s.slice(0, sep));
    }
  } catch { /* swallow */ }
  return out;
}

export async function pinnedCount(): Promise<number> {
  let db: IDBDatabase;
  try { db = await openDB(); } catch { return 0; }
  try {
    const tx = db.transaction(STORE, 'readonly');
    return await req<number>(tx.objectStore(STORE).count());
  } catch { return 0; }
}

/// Sum of every pinned row's stored blob size. Uses the duplicated
/// `size` column so we don't have to read every blob payload — keeps
/// the summary screen instant even with a few hundred entries.
export async function pinnedByteCount(): Promise<number> {
  let db: IDBDatabase;
  try { db = await openDB(); } catch { return 0; }
  try {
    const tx = db.transaction(STORE, 'readonly');
    const rows = await req<PinnedRow[]>(tx.objectStore(STORE).getAll());
    let total = 0;
    for (const r of rows) total += r.size ?? r.blob?.size ?? 0;
    return total;
  } catch { return 0; }
}

export async function clearPinned(): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORE, 'readwrite');
  await req(tx.objectStore(STORE).clear());
  await txDone(tx);
}

// Wait for a write transaction to actually commit. Without this the
// outer promise resolves on the request callback, before the txn has
// flushed — fine for in-page reads but flaky across reloads.
function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction error'));
  });
}
